import { parseSalaryValue } from "@/helpers/utils.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeeCompensationLineFormValue } from "@/pages/employees/employee/components/employee-compensation-form.types.ts";
import {
  ApiCompensationComponent,
  ApiCompensationComponentKind,
} from "@/types/compensation-component.types.ts";
import { getEmployeeCompensationPerTypes } from "@/types/employees/employee-compensation.types.ts";
import {
  Avatar,
  Button,
  ButtonIcon,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface EmployeeCompensationLineItemProps {
  line: EmployeeCompensationLineFormValue;
  component?: ApiCompensationComponent;
  changeLabel?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

const getComponentIcon = (kind: ApiCompensationComponentKind) => {
  switch (kind) {
    case ApiCompensationComponentKind.deduction:
      return "fa-light fa-circle-minus";
    case ApiCompensationComponentKind.employer_contribution:
      return "fa-light fa-building-columns";
    default:
      return "fa-light fa-money-bill-wave";
  }
};

export function EmployeeCompensationLineItem({
  line,
  component,
  changeLabel,
  onEdit,
  onDelete,
}: EmployeeCompensationLineItemProps) {
  const { t } = useTranslation();
  const { renderCurrency } = useFormatter();
  const [expanded, setExpanded] = useState(false);
  const periodTitle = getEmployeeCompensationPerTypes().find(
    (item) => item.id === line.per_type
  )?.title;
  const renderAmount = (amount: EmployeeCompensationLineFormValue["amount_gross"]) => {
    const parsed = parseSalaryValue(amount);
    return typeof parsed !== "number" || Number.isNaN(parsed)
      ? "-"
      : renderCurrency(String(parsed), line.currency_code);
  };

  return (
    <Stack className="bg-dark/5 border border-dark/5 rounded-xs px-4 py-2" gap="md">
      <ListItem
        className="border-0"
        icon={
          <Avatar
            icon={
              <FontAwesomeIcon
                icon={getComponentIcon(component?.kind ?? ApiCompensationComponentKind.earning)}
              />
            }
          />
        }
        title={component?.title ?? t("core:compensation_line_component")}
        captionSlot={
          <DotSeparator>
            <Tooltip>
              <TooltipTrigger asChild>
                <Text>{renderAmount(line.amount_gross)}</Text>
              </TooltipTrigger>
              <TooltipContent>{t("core:compensation_line_gross")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Text>{renderAmount(line.amount_net)}</Text>
              </TooltipTrigger>
              <TooltipContent>{t("core:compensation_line_net")}</TooltipContent>
            </Tooltip>
            {component?.is_off_payroll && (
              <Text className="text-secondary">
                {t("core:compensation_component_is_off_payroll")}
              </Text>
            )}
            {changeLabel && <Text className="font-semibold text-warning">{changeLabel}</Text>}
          </DotSeparator>
        }
        valueSlot={
          <ButtonIcon
            variant="plain"
            onClick={() => setExpanded((value) => !value)}
            icon={
              <FontAwesomeIcon icon={`fa-light ${expanded ? "fa-angle-down" : "fa-angle-right"}`} />
            }
          />
        }
      />
      {expanded && (
        <Stack gap="md">
          <Stack>
            <ListItem
              preTitle={t("core:compensation_component_kind")}
              captionSlot={t(
                `core:compensation_component_kind_${component?.kind ?? ApiCompensationComponentKind.earning}`
              )}
            />
            <ListItem
              preTitle={t("core:compensation_line_gross")}
              captionSlot={renderAmount(line.amount_gross)}
            />
            <ListItem
              preTitle={t("core:compensation_line_net")}
              captionSlot={renderAmount(line.amount_net)}
            />
            <ListItem preTitle={t("common:currency")} captionSlot={line.currency_code ?? "-"} />
            <ListItem preTitle={t("common:period")} captionSlot={periodTitle ?? "-"} />
            {line.description && (
              <ListItem
                preTitle={t("common:description")}
                captionSlot={<span className="whitespace-pre-wrap">{line.description}</span>}
              />
            )}
          </Stack>
          {(onEdit || onDelete) && (
            <Stack direction="horizontal" gap="sm" className="pb-2">
              {onEdit && (
                <Button variant="secondary" onClick={onEdit}>
                  {t("common:edit")}
                </Button>
              )}
              {onDelete && (
                <DropdownMenu
                  trigger={
                    <ButtonIcon
                      variant="secondary"
                      icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
                    />
                  }
                >
                  <DropdownMenuItem
                    variant="menuDanger"
                    onClick={onDelete}
                    title={t("common:delete")}
                  />
                </DropdownMenu>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
